import { create } from "zustand";
import { CollectionCard } from "modules/collections/details/model/collection";

interface EditCollectionCardModal {
  open: boolean;
  card: CollectionCard | null;
}

interface AddToTradeListModal {
  open: boolean;
  card: CollectionCard | null;
}

interface RemoveFromTradeListModal {
  open: boolean;
  card: CollectionCard | null;
}

interface CollectionsStore {
  // === Modal State ===
  editCardModal: EditCollectionCardModal;
  addToTradeListModal: AddToTradeListModal;
  removeFromTradeListModal: RemoveFromTradeListModal;

  // === Preferences ===
  closeAfterAdd: boolean;
  setCloseAfterAdd: (value: boolean) => void;

  // === Modal Actions ===
  setEditCardModal: (modal: EditCollectionCardModal) => void;
  openEditCardModal: (card: CollectionCard) => void;
  closeEditCardModal: () => void;

  setAddToTradeListModal: (modal: AddToTradeListModal) => void;
  openAddToTradeListModal: (card: CollectionCard) => void;
  closeAddToTradeListModal: () => void;

  setRemoveFromTradeListModal: (modal: RemoveFromTradeListModal) => void;
  openRemoveFromTradeListModal: (card: CollectionCard) => void;
  closeRemoveFromTradeListModal: () => void;

  // === Reset All ===
  reset: () => void;
}

export const useCollectionsStore = create<CollectionsStore>((set) => ({
  // === Initial State ===
  editCardModal: { open: false, card: null },
  addToTradeListModal: { open: false, card: null },
  removeFromTradeListModal: { open: false, card: null },
  closeAfterAdd: true,
  setCloseAfterAdd: (value) => set({ closeAfterAdd: value }),

  // === Modal Actions ===
  setEditCardModal: (modal) => set({ editCardModal: modal }),
  openEditCardModal: (card) => set({ editCardModal: { open: true, card } }),
  closeEditCardModal: () =>
    set((state) => ({
      editCardModal: { ...state.editCardModal, open: false },
    })),

  setAddToTradeListModal: (modal) => set({ addToTradeListModal: modal }),
  openAddToTradeListModal: (card) =>
    set({ addToTradeListModal: { open: true, card } }),
  closeAddToTradeListModal: () =>
    set((state) => ({
      addToTradeListModal: { ...state.addToTradeListModal, open: false },
    })),

  setRemoveFromTradeListModal: (modal) =>
    set({ removeFromTradeListModal: modal }),
  openRemoveFromTradeListModal: (card) =>
    set({ removeFromTradeListModal: { open: true, card } }),
  closeRemoveFromTradeListModal: () =>
    set((state) => ({
      removeFromTradeListModal: {
        ...state.removeFromTradeListModal,
        open: false,
      },
    })),

  // === Reset All ===
  reset: () =>
    set({
      editCardModal: { open: false, card: null },
      addToTradeListModal: { open: false, card: null },
      removeFromTradeListModal: { open: false, card: null },
    }),
}));

// ========================================
// Semantic Selector Hooks (NO shallow!)
// ========================================

/**
 * Hook for edit collection card modal state and actions
 */
export const useEditCollectionCardModal = () => {
  const modal = useCollectionsStore((state) => state.editCardModal);
  const setModal = useCollectionsStore((state) => state.setEditCardModal);
  const openModal = useCollectionsStore((state) => state.openEditCardModal);
  const closeModal = useCollectionsStore((state) => state.closeEditCardModal);

  return { modal, setModal, openModal, closeModal };
};

/**
 * Hook for add to trade list modal state and actions
 */
export const useAddToTradeListModal = () => {
  const modal = useCollectionsStore((state) => state.addToTradeListModal);
  const setModal = useCollectionsStore((state) => state.setAddToTradeListModal);
  const openModal = useCollectionsStore(
    (state) => state.openAddToTradeListModal
  );
  const closeModal = useCollectionsStore(
    (state) => state.closeAddToTradeListModal
  );

  return { modal, setModal, openModal, closeModal };
};

/**
 * Hook for remove from trade list modal state and actions
 */
export const useRemoveFromTradeListModal = () => {
  const modal = useCollectionsStore((state) => state.removeFromTradeListModal);
  const setModal = useCollectionsStore(
    (state) => state.setRemoveFromTradeListModal
  );
  const openModal = useCollectionsStore(
    (state) => state.openRemoveFromTradeListModal
  );
  const closeModal = useCollectionsStore(
    (state) => state.closeRemoveFromTradeListModal
  );

  return { modal, setModal, openModal, closeModal };
};

/**
 * Hook for "close after adding" preference
 */
export const useCloseAfterAdd = () => {
  const closeAfterAdd = useCollectionsStore((state) => state.closeAfterAdd);
  const setCloseAfterAdd = useCollectionsStore(
    (state) => state.setCloseAfterAdd
  );

  return { closeAfterAdd, setCloseAfterAdd };
};
